import React from 'react'
import styled from 'styled-components'
import Text from 'core-system/Text/Text'
import palette from 'core-system/Themes/palette'
import { EmployeeSegmentsData } from '../../siteDetailTypes'

const Container = styled.div`
  padding: 1.5rem 0;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`

const Title = styled(Text)`
  font-weight: 600;
`

const AddButton = styled.button`
  background-color: ${palette.primary.pink800};
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.8125rem;
  font-weight: 500;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${palette.primary.pink900};
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.div`
  background-color: ${palette.white};
  border: 1px solid ${palette.grey.grey200};
  border-radius: 0.75rem;
  padding: 1.25rem;
`

const CardTitle = styled(Text)`
  font-weight: 600;
  margin-bottom: 1rem;
`

const StackedBarContainer = styled.div`
  display: flex;
  width: 100%;
  height: 1.5rem;
  border-radius: 0.375rem;
  overflow: hidden;
  margin-bottom: 1.25rem;
`

const BarSegment = styled.div<{ $width: number; $color: string }>`
  width: ${({ $width }) => $width}%;
  height: 100%;
  background-color: ${({ $color }) => $color};
`

const TiersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`

const TierRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const TierInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const TierDot = styled.div<{ $color: string }>`
  width: 0.75rem;
  height: 0.75rem;
  border-radius: 0.125rem;
  background-color: ${({ $color }) => $color};
`

const TierName = styled(Text)`
  font-size: 0.875rem;
`

const TierStats = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const TierEmployees = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const TierStipend = styled(Text)`
  font-weight: 600;
  color: ${palette.primary.pink800};
  font-size: 0.875rem;
`

const PolicyLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.75rem;
  margin-bottom: 0.5rem;
`

const PolicyDescription = styled(Text)`
  font-size: 0.875rem;
  line-height: 1.5;
  margin-bottom: 1.25rem;
`

const StatsCard = styled.div`
  background-color: ${palette.grey.grey50};
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
`

const StatsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const StatsLabel = styled(Text)`
  color: ${palette.text.secondary};
  font-size: 0.875rem;
`

const StatsValue = styled(Text)`
  font-weight: 600;
  font-size: 1rem;
  color: ${palette.primary.pink800};
`

const AvgRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid ${palette.grey.grey200};
`

const EditButton = styled.button`
  width: 100%;
  background-color: ${palette.white};
  color: ${palette.text.primary};
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border: 1px solid ${palette.grey.grey300};
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-top: 0.5rem;

  &:hover {
    background-color: ${palette.grey.grey100};
  }
`

interface EmployeeSegmentsTabProps {
  data: EmployeeSegmentsData
}

const EmployeeSegmentsTab = ({ data }: EmployeeSegmentsTabProps) => {
  const totalEmployees = data.tiers.reduce(
    (sum, tier) => sum + tier.employeeCount,
    0
  )

  return (
    <Container>
      <Header>
        <Title variant='body1'>Employee Segments</Title>
        <AddButton>+ Add Segment</AddButton>
      </Header>

      <ContentGrid>
        <Card>
          <CardTitle variant='body1'>RTO Commitment Tiers</CardTitle>

          <StackedBarContainer>
            {data.tiers.map((tier) => (
              <BarSegment
                key={tier.id}
                $width={(tier.employeeCount / totalEmployees) * 100}
                $color={tier.color}
              />
            ))}
          </StackedBarContainer>

          <TiersList>
            {data.tiers.map((tier) => (
              <TierRow key={tier.id}>
                <TierInfo>
                  <TierDot $color={tier.color} />
                  <TierName>{tier.name}</TierName>
                </TierInfo>
                <TierStats>
                  <TierEmployees>{tier.employeeCount} employees</TierEmployees>
                  <TierStipend>${tier.stipendAmount}/mo</TierStipend>
                </TierStats>
              </TierRow>
            ))}
          </TiersList>
        </Card>

        <Card>
          <CardTitle variant='body1'>Stipend Rules</CardTitle>

          <PolicyLabel>Current Policy</PolicyLabel>
          <PolicyDescription>{data.rules.description}</PolicyDescription>

          <StatsCard>
            <StatsRow>
              <StatsLabel>Total Monthly Stipend Spend</StatsLabel>
              <StatsValue>
                ${data.rules.totalMonthlySpend.toLocaleString()}
              </StatsValue>
            </StatsRow>
          </StatsCard>

          <AvgRow>
            <StatsLabel>Avg. Stipend per Employee</StatsLabel>
            <Text fontWeight={600}>${data.rules.avgPerEmployee}</Text>
          </AvgRow>

          <EditButton>Edit Segment Rules</EditButton>
        </Card>
      </ContentGrid>
    </Container>
  )
}

export default React.memo(EmployeeSegmentsTab)
